import { Grid } from "@mui/material";

import { FormContainer, Treturn, useFetch } from "@4uhub/lib4uhub";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { AutomationRulesProvider } from "../../../../../../store/contexts/AutomationRulesContext";
import { IMarComTicketAutomationRulesProps } from "./models";
import { ITicketAutomation } from "../../../../../../models/ticket-automation-rules";
import {
  createMarComTicketAutomationRules,
  getMarComTicketAutomationRulesById,
  updateMarComTicketAutomationRulesById,
} from "../../../../../../services/marComTicketAutomationRules.service";
import {
  marComTicketAutomationRulesSchema,
  TMarComTicketAutomationRulesForm,
} from "./marComTicketAutomationRulesSchema";
import { BasicData } from "./BasicData/BasicData";
const translationPath = "page.4uMarCom.automationRules.ticketautomationRules.";

const MarComTicketAutomationRules = ({
  id,
  marComAutomationRuleId,
  onBackButton,
}: IMarComTicketAutomationRulesProps) => {
  const { t } = useTranslation();

  const [ticketAutomation, setTicketAutomation] =
    useState<ITicketAutomation | null>(null);

  const { sendRequest: save, loading: sLoading } = useFetch(
    createMarComTicketAutomationRules
  );

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateMarComTicketAutomationRulesById
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getMarComTicketAutomationRulesById
  );

  const saveHandler = (v: TMarComTicketAutomationRulesForm) => {
    return save({
      ...v,
      marComAutomationRuleId: marComAutomationRuleId ?? "",
      ticketAutomationId: v.ticketAutomation ? v.ticketAutomation.id : "",
      ticketAutomationTemplateId: v.ticketAutomationTemplate
        ? v.ticketAutomationTemplate.id
        : undefined,
      actionAfter24HoursId: v.actionAfter24Hours
        ? v.actionAfter24Hours.id
        : "",
      ticketServiceId: v.ticketService.id,
      ticketEventId: v.ticketEvent ? v.ticketEvent.id : "",
      ticketChannelStepRobotId: v.ticketChannelStepRobot
        ? v.ticketChannelStepRobot.id
        : undefined,
      ticketChannelStepAttendantId: v.ticketChannelStepAttendant
        ? v.ticketChannelStepAttendant.id
        : undefined,
      ticketChannelStepPendentId: v.ticketChannelStepPendent
        ? v.ticketChannelStepPendent.id
        : undefined,
      ticketChannelStepWhenErrorId: v.ticketChannelStepWhenError
        ? v.ticketChannelStepWhenError.id
        : undefined,
      ticketChannelStepWhenBlackListId: v.ticketChannelStepWhenBlacklist
        ? v.ticketChannelStepWhenBlacklist.id
        : undefined,
      initialDate: v.initialDate?.toISOString().substring(0, 10),
      endDate: v.endDate?.toISOString().substring(0, 10),
      ticketChannels: v.ticketChannels
        ? v.ticketChannels.map((priv) => priv.id)
        : undefined,
    });
  };
  const updateHandler = (v: TMarComTicketAutomationRulesForm, id: string) => {
    return update({
      item: {
        ...v,
        marComAutomationRuleId: marComAutomationRuleId ?? "",
        ticketAutomationTemplateId: v.ticketAutomationTemplate
          ? v.ticketAutomationTemplate.id
          : undefined,
        actionAfter24HoursId: v.actionAfter24Hours
          ? v.actionAfter24Hours.id
          : "",
        ticketServiceId: v.ticketService.id,
        ticketEventId: v.ticketEvent ? v.ticketEvent.id : "",
        ticketChannelStepRobotId: v.ticketChannelStepRobot
          ? v.ticketChannelStepRobot.id
          : undefined,
        ticketChannelStepAttendantId: v.ticketChannelStepAttendant
          ? v.ticketChannelStepAttendant.id
          : undefined,
        ticketChannelStepPendentId: v.ticketChannelStepPendent
          ? v.ticketChannelStepPendent.id
          : undefined,
        ticketChannelStepWhenErrorId: v.ticketChannelStepWhenError
          ? v.ticketChannelStepWhenError.id
          : undefined,
        ticketChannelStepWhenBlackListId: v.ticketChannelStepWhenBlacklist
          ? v.ticketChannelStepWhenBlacklist.id
          : undefined,
        initialDate: v.initialDate?.toISOString().substring(0, 10),
        endDate: v.endDate?.toISOString().substring(0, 10),
        ticketChannels: v.ticketChannels
          ? v.ticketChannels.map((priv) => priv.id)
          : undefined,
      },
      id: id,
    });
  };

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TMarComTicketAutomationRulesForm>> => {
      const { data, success } = await get(id);
      if (data) {
        setTicketAutomation(data.ticketAutomation);

        const newData: TMarComTicketAutomationRulesForm = {
        ...data,
        initialDate: new Date(`${data.initialDate}T12:00:00Z`),
        endDate: new Date(`${data.endDate}T12:00:00Z`),
        ticketAutomation: data.ticketAutomation,
        actionAfter24Hours: data.actionAfter24Hours ? data.actionAfter24Hours : null,
        ticketChannelAttendant: data.ticketChannelStepAttendant
        ? data.ticketChannelStepAttendant.ticketChannel
        : null,
        ticketChannelRobot: data.ticketChannelStepRobot
          ? data.ticketChannelStepRobot.ticketChannel
          : null,
        ticketChannelPendent: data.ticketChannelStepPendent
          ? data.ticketChannelStepPendent.ticketChannel
          : null,
        ticketChannelWhenError: data.ticketChannelStepWhenError
          ? data.ticketChannelStepWhenError.ticketChannel
          : null,
        ticketChannelWhenBlacklist: data.ticketChannelStepWhenBlackList
          ? data.ticketChannelStepWhenBlackList.ticketChannel
          : null,
        ticketChannelStepWhenBlacklist: data.ticketChannelStepWhenBlackList,
        };
        return { data: newData, success: true };
      }
      return { data, success };
    },
    [get]
  );

  const loading = sLoading || uLoading || gLoading;

  return (
    <AutomationRulesProvider>
      <>
        <FormContainer<TMarComTicketAutomationRulesForm>
          saveFunction={saveHandler}
          updateFunction={updateHandler}
          getFunction={getHandler}
          loading={loading}
          title={t(translationPath + "automation_rule")}
          schema={marComTicketAutomationRulesSchema}
          subtitle={t(translationPath + "new_automation_rule")}
          subtitleWatchField={"name"}
          backButtonOnClick={onBackButton}
          redirectOnSave={false}
          onSuccess={onBackButton}
          formActionsOnBottom={false}
          marginBottom={5}
          isModalForm={true}
          propId={id}
          customHeight={"fit-content"}
          defaultValues={{ ticketChannels: [] }}
          fields={() => (
            <Grid container spacing={2}>
              <BasicData ticketAutomation={ticketAutomation} />
            </Grid>
          )}
        />
      </>
    </AutomationRulesProvider>
  );
};

export default MarComTicketAutomationRules;
